import { extractTextFromPDF } from './pdfProcessor';

// AI-powered PDF data extraction using OpenAI API
export const processPDFWithAI = async (file) => {
  try {
    console.log('🤖 Starting AI-powered PDF processing...');
    
    // First, extract text from PDF
    const pdfText = await extractTextFromPDF(file);
    console.log('📄 Extracted PDF text:', pdfText);
    
    if (!pdfText || pdfText.trim().length === 0) {
      throw new Error('No text could be extracted from the PDF');
    }
    
    // Call OpenAI API to extract structured data
    const extractedData = await extractInvoiceDataWithAI(pdfText);
    
    console.log('✅ AI extraction completed:', extractedData);
    return extractedData;
    
  } catch (error) {
    console.error('❌ AI PDF processing failed:', error);
    throw error;
  }
};

// Extract structured invoice data using OpenAI API
const extractInvoiceDataWithAI = async (pdfText) => {
  try {
    // Check if OpenAI API key is available
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      console.warn('⚠️ OpenAI API key not found, falling back to regex extraction');
      return await fallbackToRegexExtraction(pdfText);
    }
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini', // Cheaper model, good for structured data extraction
        messages: [
          {
            role: 'system',
            content: `You are an expert at extracting structured data from invoice PDFs. 
            Extract the following information and return it as a JSON object:
            
            {
              "invoiceNumber": "string (invoice number or reference)",
              "date": "string (invoice date in YYYY-MM-DD format)",
              "supplier": "string (supplier/company name)",
              "supplierAddress": "string (supplier address)",
              "supplierEmail": "string (supplier email)",
              "supplierPhone": "string (supplier phone)",
              "client": "string (client/customer name)",
              "clientAddress": "string (client address)",
              "clientEmail": "string (client email)",
              "clientPhone": "string (client phone)",
              "totalHT": "number (total excluding tax)",
              "tva": "number (tax amount)",
              "totalTTC": "number (total including tax)",
              "products": [
                {
                  "name": "string (product name)",
                  "description": "string (product description)",
                  "quantity": "number (quantity)",
                  "priceHT": "number (unit price excluding tax)",
                  "tva": "number (tax rate percentage)",
                  "remise": "number (discount amount)"
                }
              ]
            }
            
            Rules:
            - Extract only the most relevant information
            - For dates, convert to YYYY-MM-DD format
            - For prices, extract as numbers (not strings)
            - For products, extract each line item from the invoice table
            - If a field is not found, use null or empty string
            - Be precise and accurate with the extraction`
          },
          {
            role: 'user',
            content: `Please extract the invoice data from this PDF text:\n\n${pdfText}`
          }
        ],
        temperature: 0.1, // Low temperature for consistent, structured output
        max_tokens: 2000
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`OpenAI API error: ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    const aiResponse = data.choices[0]?.message?.content;
    
    if (!aiResponse) {
      throw new Error('No response from OpenAI API');
    }
    
    // Parse the AI response as JSON
    let extractedData;
    try {
      // Clean the response to extract JSON
      const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        extractedData = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('No JSON found in AI response');
      }
    } catch (parseError) {
      console.error('Failed to parse AI response as JSON:', parseError);
      console.log('AI Response:', aiResponse);
      throw new Error('Failed to parse AI response as JSON');
    }
    
    // Validate and clean the extracted data
    return validateAndCleanAIData(extractedData);
    
  } catch (error) {
    console.error('AI extraction failed:', error);
    console.log('Falling back to regex extraction...');
    return await fallbackToRegexExtraction(pdfText);
  }
};

// Validate and clean the data extracted by AI
const validateAndCleanAIData = (data) => {
  const cleaned = {
    invoiceNumber: data.invoiceNumber || '',
    date: data.date || '',
    supplier: data.supplier || '',
    supplierAddress: data.supplierAddress || '',
    supplierEmail: data.supplierEmail || '',
    supplierPhone: data.supplierPhone || '',
    client: data.client || '',
    clientAddress: data.clientAddress || '',
    clientEmail: data.clientEmail || '',
    clientPhone: data.clientPhone || '',
    totalHT: parseFloat(data.totalHT) || 0,
    tva: parseFloat(data.tva) || 0,
    totalTTC: parseFloat(data.totalTTC) || 0,
    products: []
  };
  
  // Clean and validate products
  if (Array.isArray(data.products)) {
    cleaned.products = data.products.map(product => ({
      name: product.name || '',
      description: product.description || '',
      quantity: parseInt(product.quantity) || 1,
      priceHT: parseFloat(product.priceHT) || 0,
      tva: parseFloat(product.tva) || 20,
      remise: parseFloat(product.remise) || 0
    })).filter(product => product.name && product.name.trim() !== '');
  }
  
  // Calculate totals if missing
  if (cleaned.totalHT === 0 && cleaned.products.length > 0) {
    cleaned.totalHT = cleaned.products.reduce((sum, product) => {
      const productTotal = (product.priceHT * product.quantity) - product.remise;
      return sum + productTotal;
    }, 0);
  }
  
  if (cleaned.totalTTC === 0 && cleaned.totalHT > 0) {
    const totalTVA = cleaned.products.reduce((sum, product) => {
      const productTotal = (product.priceHT * product.quantity) - product.remise;
      const productTVA = productTotal * (product.tva / 100);
      return sum + productTVA;
    }, 0);
    cleaned.tva = totalTVA;
    cleaned.totalTTC = cleaned.totalHT + totalTVA;
  }
  
  return cleaned;
};

// Fallback to regex extraction if AI fails
const fallbackToRegexExtraction = async (pdfText) => {
  console.log('🔄 Falling back to regex extraction...');
  
  // Import the original regex-based extraction
  const { extractInvoiceData } = await import('./pdfProcessor');
  return await extractInvoiceData(pdfText);
};

// Check if OpenAI API is available
export const isAIAvailable = () => {
  return !!process.env.REACT_APP_OPENAI_API_KEY;
};

// Get AI usage instructions
export const getAIUsageInstructions = () => {
  return {
    available: isAIAvailable(),
    instructions: isAIAvailable() 
      ? "AI-powered extraction is enabled. Add your OpenAI API key to .env file as REACT_APP_OPENAI_API_KEY"
      : "To enable AI-powered extraction, add your OpenAI API key to .env file as REACT_APP_OPENAI_API_KEY"
  };
};


